import '../stepsView.scss';

import React, { useState, useEffect } from 'react';
import { RoadmapNavigation } from 'components/steps/RoadmapNavigation/RoadmapNavigation';
import RARadioButtonStep from 'components/RAComponents/RARadioButtonStep/RARadioButtonStep';
import RAModal from 'components/RAComponents/commons/RAModal/RAModal';
import { GdprLink } from '../ GdprLink';
import { formatText } from 'core/i18n';
import UtagHelper from 'utilities/utagHelper';
import { PATH_GBI_TI, PATH_ROBO_TI } from 'constants/uTagConstants/paths';
import { SUITABILITY_TEST_PROFILE_MODEL_ANSWERS, SUITABILITY_TEST_STEP_3 } from 'constants/RAConstants';
import {
    TEST_PROCESS_STEP_STEP_1,
    TEST_PROCESS_STEP_STEP_2,
    TEST_PROCESS_STEP_STEP_3,
    TEST_PROCESS_STEP_STEP_10,
    EVENT_DETAIL_QUESTION_LOAD,
    EVENT_CATEGORY_SUITABILITY_TEST,
    EVENT_DETAIL_BUTTON_NEXT,
    EVENT_DETAIL_BUTTON_PREV,
    EVENT_DETAIL_VERIFY_MODAL,
    EVENT_DETAIL_VERIFY,
    EVENT_DETAIL_BUTTON_CLOSE,
    EVENT_DETAIL_FINISH,
} from 'constants/uTagConstants/RAConstants';
import { GBI } from 'constants/userProfile';
import RASuitabilityTestKnowledgeAlert from '../RASuitabilityTestKnowledgeAlert/RASuitabilityTestKnowledgeAlert';

export const RAKnowledgeExperienceSteps = ({
    RATestWizard,
    userProfile,
    immSuitabilityTest,
    raSuitabilityTest,
    raTestActions,
    stepsActions,
    formActions,
    modalCancelActions,
}) => {
    const [showModal, setShowModal] = useState(false);

    const getCurrentStepAndSubStep = () => {
        const { currentStep } = RATestWizard;
        const { currentSubStep } = RATestWizard;

        return {
            currentStep,
            currentSubStep,
        };
    };
    const getIsWithWarning = () => {
        const { currentStep, currentSubStep } = getCurrentStepAndSubStep();

        return RATestWizard.steps?.[currentStep].steps?.[currentSubStep].withWarning || false;
    };

    const recordEvent = (eventDetail = '') => {
        const { currentStep, currentSubStep } = getCurrentStepAndSubStep();
        let pathForTag = PATH_ROBO_TI;
        let eventAction = '';

        if (userProfile.investmentType === GBI) {
            pathForTag = PATH_GBI_TI;
        }

        if (currentStep === 0) {
            if (currentSubStep === 0) {
                eventAction = TEST_PROCESS_STEP_STEP_1;
            } else if (currentSubStep === 1) {
                eventAction = TEST_PROCESS_STEP_STEP_2;
            } else {
                eventAction = TEST_PROCESS_STEP_STEP_3;
            }
        } else if (currentStep === 2 && currentSubStep === 2) {
            eventAction = TEST_PROCESS_STEP_STEP_10;
        }

        UtagHelper.recordEventRoboGBI(
            pathForTag,
            EVENT_CATEGORY_SUITABILITY_TEST,
            eventAction,
            eventDetail,
            userProfile
        );
    };

    useEffect(() => {
        recordEvent(EVENT_DETAIL_QUESTION_LOAD);
    }, []);

    useEffect(() => {
        recordEvent(EVENT_DETAIL_QUESTION_LOAD);
    }, [getCurrentStepAndSubStep().currentStep, getCurrentStepAndSubStep().currentSubStep]);

    const getQuestionId = () => {
        const { currentStep, currentSubStep } = getCurrentStepAndSubStep();

        return RATestWizard.steps?.[currentStep].steps?.[currentSubStep].question;
    };

    const getSelectedLabel = () => {
        const questionId = getQuestionId();
        const selectedOption = userProfile?.[SUITABILITY_TEST_PROFILE_MODEL_ANSWERS]?.[questionId];
        const question = immSuitabilityTest.getIn(['questions', questionId]);
        const answers = question.get('answers').toJS();
        const selectedLabel = answers.find(answer => answer.id === selectedOption)?.label.title;

        return selectedLabel;
    };

    const onHandleCancelButton = () => {
        recordEvent(EVENT_DETAIL_BUTTON_CLOSE);
        modalCancelActions.showModalCancel();
    };

    const onHandlePrevButton = () => {
        recordEvent(EVENT_DETAIL_BUTTON_PREV);
        raTestActions.prevStep(getCurrentStepAndSubStep());
    };

    const toggleModal = () => {
        setShowModal(prevState => !prevState);
    };

    const isFirstQuestion = () => {
        const { currentStep, currentSubStep } = getCurrentStepAndSubStep();

        return currentStep === 0 && currentSubStep === 0;
    };

    const onHandleContinueButton = () => {
        window.scrollTo(0, 0);
        const { currentStep, currentSubStep } = getCurrentStepAndSubStep();
        const { lastSubStep } = RATestWizard;
        const finalized = RATestWizard.steps?.[currentStep].steps?.[currentSubStep].finished;
        const selectedLabel = getSelectedLabel();
        const data = {
            step: currentStep,
            substep: currentSubStep,
        };

        if (currentStep === 2 && lastSubStep) {
            raTestActions.finishStep(data);
            stepsActions.enablePreviousSteps({
                index: 2,
            });
            recordEvent(`${EVENT_DETAIL_FINISH}, ${selectedLabel}`);
        } else if (finalized) {
            raTestActions.nextStep(data);
            recordEvent(`${EVENT_DETAIL_BUTTON_NEXT}, ${selectedLabel}`);
        } else if (lastSubStep) {
            raTestActions.finishStep(data);
            recordEvent(`${EVENT_DETAIL_BUTTON_NEXT}, ${selectedLabel}`);
        } else {
            isFirstQuestion && raTestActions.removeStepFromSkippedSteps();
            raTestActions.finishSubStep(data);
            recordEvent(`${EVENT_DETAIL_BUTTON_NEXT}, ${selectedLabel}`);
        }
    };

    const handleOnClick = (eventDetail = '') => {
        recordEvent(eventDetail);
    };

    const handleValidateStep = step => {
        const selectedLabel = getSelectedLabel();
        recordEvent(selectedLabel);
        raSuitabilityTest.validateStep(step);
    };

    const getContent = questionId => {
        const question = immSuitabilityTest.getIn(['questions', questionId]);
        const title = question.get('title');
        const answers = question.get('answers').toJS();
        const profileModel = `${SUITABILITY_TEST_PROFILE_MODEL_ANSWERS}.${questionId}`;

        const tooltipContentByQuestionId = {
            101: formatText('suitabilityTest-tooltipFirstQuestion'),
            102: formatText('suitabilityTest-tooltipSecondQuestion'),
            103: formatText('suitabilityTest-tooltipThirdQuestion'),
            323: formatText('suitabilityTest-tooltipTenQuestion'),
        };

        const tooltipContent = tooltipContentByQuestionId[questionId] || '';

        return (
            <RARadioButtonStep
                disabled={false}
                formActions={formActions}
                profileModel={profileModel}
                radioButtons={answers}
                titleText={title}
                tooltipContent={tooltipContent}
                userProfile={userProfile}
                model={questionId}
                validateStep={handleValidateStep}
                step={SUITABILITY_TEST_STEP_3}
                onClickTooltip={handleOnClick}
            />
        );
    };

    const isDisabled = questionID => {
        return !userProfile?.RASuitabilityTest?.answers?.[questionID];
    };

    const closeButtonAction = () => {
        recordEvent(`${EVENT_DETAIL_VERIFY_MODAL} ${EVENT_DETAIL_BUTTON_CLOSE}`);
        toggleModal();
    };

    const renderGdpr = () => {
        return isFirstQuestion() ? <GdprLink recordEvent={recordEvent} /> : null;
    };

    const handleModalContinueAction = () => {
        recordEvent(`${EVENT_DETAIL_VERIFY_MODAL} ${EVENT_DETAIL_VERIFY}`);
        toggleModal();
        raTestActions.skipToNextStep();
    };

    return (
        <div>
            <div className="steps-view">
                {getIsWithWarning() && <RASuitabilityTestKnowledgeAlert />}
                {getContent(getQuestionId())}
                <RAModal
                    title={formatText('suitabilityTest-yourSuitabilityTestResult')}
                    text={formatText('suitabilityTest-financeProfessionalAdvise')}
                    firstButton={formatText('button-understood')}
                    firstAction={handleModalContinueAction}
                    show={showModal}
                    hide={closeButtonAction}
                />
            </div>
            {isFirstQuestion() && (
                <div className="consents-advise">
                    <p>{formatText('suitabilityTest-consentsText')}</p>
                </div>
            )}
            {renderGdpr()}
            <hr />
            <div className="steps-view__footer">
                <RoadmapNavigation
                    buttonContinueType="submit"
                    buttonContinueAction={onHandleContinueButton}
                    buttonContinueText={formatText('suitabilityTest-next')}
                    buttonPrevText={formatText('suitabilityTest-prev')}
                    buttonPrevHidden={isFirstQuestion()}
                    buttonCancelAction={onHandleCancelButton}
                    buttonCancelText={formatText('suitabilityTest-getOut')}
                    buttonPrevAction={onHandlePrevButton}
                    buttonContinueDisabled={isDisabled(getQuestionId())}
                />
            </div>
        </div>
    );
};
